import { InjectionToken } from '@angular/core';
import { ISbStoriesParams, ISbStoryData } from '@storyblok/js';

/** CloudFront stores at most 50 tags per cached object; extras are dropped silently. */
export const MAX_CLOUD_FRONT_CACHE_TAGS = 50;

export const CACHE_TAG_HEADER = 'Cache-Tag';

export type StoryblokCacheTagging = 'collection' | 'items' | 'none';

export type StoryblokStoriesQuery = ISbStoriesParams & {
  /**
   * `collection` — tag the content type (`ct:article`) so listings refresh when
   * any member is published. `items` — tag each returned story id. `none` —
   * UUID lookups that only need metadata (relationships, topic page slugs).
   * Default: `collection` for content_type lists, `none` for `by_uuids`,
   * otherwise `items`.
   */
  cache_tagging?: StoryblokCacheTagging;
};

export const STORYBLOK_CACHE_TAGS = new InjectionToken<StoryblokCacheTags>('STORYBLOK_CACHE_TAGS');

export class StoryblokCacheTags {
  private readonly itemIds = new Set<number>();
  private readonly collectionTypes = new Set<string>();

  addItem(id: number | string | undefined | null) {
    const numericId = typeof id === 'number' ? id : Number(id);
    if (!Number.isFinite(numericId) || numericId <= 0) return;
    this.itemIds.add(numericId);
  }

  addCollection(contentType: string | undefined | null) {
    const tag = contentType?.trim();
    if (!tag || /[\s,]/.test(tag)) return;
    this.collectionTypes.add(tag);
  }

  toHeaderValue(): string | undefined {
    const collections = [...this.collectionTypes].sort().map(type => `ct:${type}`);
    const items = [...this.itemIds].sort((a, b) => a - b).map(id => `id:${id}`);
    const all = [...collections, ...items];
    if (!all.length) return;
    if (all.length > MAX_CLOUD_FRONT_CACHE_TAGS) {
      console.warn('STORYBLOK_CACHE_TAGS_TRUNCATED', {
        kept: MAX_CLOUD_FRONT_CACHE_TAGS,
        dropped: all.length - MAX_CLOUD_FRONT_CACHE_TAGS,
      });
    }
    return all.slice(0, MAX_CLOUD_FRONT_CACHE_TAGS).join(', ');
  }
}

export const recordStoryblokListTags = (
  tags: StoryblokCacheTags | null | undefined,
  options: Pick<StoryblokStoriesQuery, 'by_uuids' | 'content_type' | 'cache_tagging'>,
  stories: Pick<ISbStoryData, 'id'>[]
) => {
  if (!tags) return;
  const mode = options.cache_tagging ?? (options.by_uuids ? 'none' : options.content_type ? 'collection' : 'items');
  if (mode === 'none') return;
  if (mode === 'collection') {
    tags.addCollection(options.content_type);
    return;
  }
  for (const story of stories) {
    tags.addItem(story?.id);
  }
};
